import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { api } from '@/lib/api';
import type { AdminProviderSpendDailyPoint } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toUsd, fmtUsd } from './credits-format';

const WINDOWS = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
] as const;
type WindowKey = (typeof WINDOWS)[number]['key'];

// Distinct line colors for the top-spend providers (rest are grouped in the table).
const LINE_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#9333ea'];
const MAX_CHART_PROVIDERS = LINE_COLORS.length;

function windowRange(days: number): { from: string; to: string } {
  const to = new Date().toISOString().slice(0, 10);
  const d = new Date(`${to}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return { from: d.toISOString().slice(0, 10), to };
}

/**
 * Pivot sparse daily rows into one recharts point per day in [from..to]:
 * { date, [provider]: usd }. EVERY day in the range gets a point — zero-spend
 * days must appear on the axis, or the line interpolates false spend across
 * gaps. Exported for direct unit testing.
 */
export function buildChartData(
  daily: AdminProviderSpendDailyPoint[],
  chartProviders: string[],
  from: string,
  to: string,
): Array<Record<string, number | string>> {
  const byDate = new Map<string, Record<string, number | string>>();
  const cursor = new Date(`${from}T00:00:00.000Z`);
  const end = Date.parse(`${to}T00:00:00.000Z`);
  while (cursor.getTime() <= end) {
    const key = cursor.toISOString().slice(0, 10);
    const point: Record<string, number | string> = { date: key.slice(5) };
    for (const p of chartProviders) point[p] = 0;
    byDate.set(key, point);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  for (const row of daily) {
    const point = byDate.get(row.date);
    if (!point || !chartProviders.includes(row.provider)) continue;
    point[row.provider] = toUsd(row.rawCostCredits);
  }
  return [...byDate.values()];
}

/**
 * Per-provider spend + invocation volume over time, from the finalized
 * spend-window rollups (same source as the caps table — raw provider cost,
 * pre-markup). The chart shows the top spenders; the table shows every
 * provider with spend in the window.
 */
export function SpendHistoryPanel() {
  const [windowKey, setWindowKey] = useState<WindowKey>('30d');
  const days = WINDOWS.find((w) => w.key === windowKey)!.days;
  // Computed per render (NOT memoized on [days]): the range must roll over at
  // UTC midnight, so any re-render (window focus refetch, etc.) picks up the
  // new day and changes the query key.
  const { from, to } = windowRange(days);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.adminProviders.spendHistory(from, to),
    queryFn: () => api.adminGetProviderSpendHistory(from, to),
  });

  const totals = data?.totals ?? [];

  const { chartProviders, chartData } = useMemo(() => {
    const providers = (data?.totals ?? []).slice(0, MAX_CHART_PROVIDERS).map((t) => t.provider);
    return {
      chartProviders: providers,
      chartData: data ? buildChartData(data.daily, providers, data.from, data.to) : [],
    };
  }, [data]);

  const windowTotalCredits = totals.reduce((sum, t) => sum + t.rawCostCredits, 0);
  const windowTotalCalls = totals.reduce((sum, t) => sum + t.invocationCount, 0);

  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold">Provider spend history</h2>
        <div className="flex gap-1 text-xs">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowKey(w.key)}
              className={`px-2 py-1 rounded ${windowKey === w.key ? 'bg-primary text-primary-foreground' : 'border border-border'}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Finalized raw provider cost (pre-markup) and invocation counts, by UTC day.
      </p>

      {isError ? (
        <p className="text-sm text-destructive">Failed to load spend history.</p>
      ) : isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : totals.length === 0 ? (
        <p className="text-sm text-muted-foreground">No provider spend recorded in this window.</p>
      ) : (
        <>
          <div className="flex gap-6 mb-3 text-sm">
            <div>
              <span className="text-muted-foreground">Total spend: </span>
              <span className="font-semibold tabular-nums">{fmtUsd(windowTotalCredits)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Invocations: </span>
              <span className="font-semibold tabular-nums">
                {windowTotalCalls.toLocaleString()}
              </span>
            </div>
          </div>

          {chartData.length > 1 && (
            <div className="h-56 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${v}`} />
                  <Tooltip formatter={(v: number) => `$${v.toFixed(2)}`} />
                  {chartProviders.map((provider, i) => (
                    <Line
                      key={provider}
                      type="monotone"
                      dataKey={provider}
                      name={provider}
                      stroke={LINE_COLORS[i]}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-4 font-medium">Provider</th>
                  <th className="py-1.5 pr-4 font-medium text-right">Spend</th>
                  <th className="py-1.5 pr-4 font-medium text-right">Invocations</th>
                  <th className="py-1.5 font-medium text-right">Avg / call</th>
                </tr>
              </thead>
              <tbody>
                {totals.map((t) => (
                  <tr key={t.provider} className="border-b border-border last:border-0">
                    <td className="py-1.5 pr-4">{t.provider}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {fmtUsd(t.rawCostCredits)}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {t.invocationCount.toLocaleString()}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {t.invocationCount > 0 ? fmtUsd(t.rawCostCredits / t.invocationCount) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
